import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {RootState} from '../../store'
import {IFetchSOApprovalParams} from '../../api/modules/master-data/types'
import {TDetailSOContract, TOrderItemSO, TOrderSubDetail, TSOItem} from '../types'
import {IState} from './notificationSlices'

/** @deprecated gunakan IFetchSOParams */
export interface IFilter {
  so_number?: string
  incoterm?: string
  sector_id?: string
  sales_org_code?: string
  total_quantum_so?: string
  product_type?: string
  from_date?: string
  to_date?: string
  status?: string
  po_number?: string
  sip_number?: string
  customer_name?: string
  /** @deprecated gunakan price_after_ppn */
  total_price_so?: string
  payment_method?: string
  booking_code?: string
  sap_number?: string
  price_after_ppn?: string
}

export interface ISoState {
  isLoading?: boolean
  error?: string
  soOrders?: Array<any>
  filter?: IFetchSOApprovalParams
  outstandingFilter?: {
    so_number?: string
    order_number?: string
    total_quantum?: string
    sector_id?: string
    status?: string
    sales_org_code?: string
    customer_name?: string
  }
  sort?: {
    po_number_orderby: 'asc' | 'desc' | ''
    customer_name_orderby: 'asc' | 'desc' | ''
    checked: boolean
  }
  numberUrls?: Array<string>
  browseContractFilter?: {
    contract_number?: string
    customer_name?: string
    sector_id?: string
    sap_number?: string
    product_type?: string
  }
  /**
   * Ref: https://documenter.getpostman.com/view/11067051/2s83tJGqYi#59c312ec-2776-42c2-b76b-f6cb506ae7c3
   */
  detailSOContract: TDetailSOContract | null
  orderItem: null | Partial<TOrderItemSO>
  orderItems: TOrderItemSO[]
  /**
   * `soItem` ini digunakan untuk menyimpan request body endpoint update soItem
   * ref: https://documenter.getpostman.com/view/11067051/2s83tJGqYi#8d2eebf4-8e38-4d83-bdca-575889223025
   */
  soItem: TSOItem | null
}

const initialState: ISoState = {
  isLoading: false,
  error: '',
  soOrders: [],
  /** @deprecated gunakan IFetchSOApprovalParams */
  sort: {
    po_number_orderby: '',
    customer_name_orderby: '',
    checked: false,
  },
  filter: {
    so_number: '',
    incoterm: '',
    sector_id: '',
    sales_org_code: '',
    total_quantum_so: '',
    product_type: '',
    from_date: '',
    to_date: '',
    status: '',
    po_number: '',
    sip_number: '',
    customer_name: '',
    /** @deprecated gunakan price_after_ppn */
    total_price_so: '',
    payment_type: '',
    booking_code: '',
    sap_number: '',
    customer_name_orderby: '',
    po_number_orderby: '',
    price_after_ppn: '',
    per_page: 25,
  },
  outstandingFilter: {
    so_number: '',
    order_number: '',
    total_quantum: ''
  },
  browseContractFilter: {
    contract_number: '',
    customer_name: '',
    sector_id: '',
    sap_number: '',
    product_type: '',
  },
  detailSOContract: null,
  orderItem: null,
  orderItems: [],
  soItem: null,
}

/**
 * Slice
 */
const soSlice = createSlice({
  name: 'so',
  initialState,
  reducers: {
    setSoOrders: (state, action) => ({...state, orders: action.payload}),
    setFilter: (state, action) => ({...state, filter: action.payload}),
    setOutstandingFilter: (state, action) => ({...state, outstandingFilter: action.payload}),
    setSort: (state, action) => ({...state, sort: action.payload}),
    setError: (state, action) => ({...state, error: action.payload}),
    setIsLoading: (state, action) => ({...state, isLoading: action.payload}),
    setNumberUrls: (state, action) => ({...state, numberUrls: action.payload}),
    resetFilter: (state) => ({...state, filter: initialState.filter, sort: initialState.sort}),
    setBrowseContractFilter: (state, action) => ({...state, browseContractFilter: action.payload}),
    setDetailSOContract: (state, action) => ({...state, detailSOContract: action.payload}),
    setOrderItem: (state, action: PayloadAction<ISoState['orderItem']>) => {
      state.orderItem = action.payload

      // NOTE: untuk soItem beberapa datanya bisa diambil dari orderItem, populate soItem!
      if (action.payload !== null) {
        const {
          id: id_soItem,
          plant,
          plant_desc,
          price,
          shipping_tolerance,
          sloc,
          sloc_description,
          notes,
          product_spec,
          quantity,
          route,
          route_desc,
          incoterm1,
          incoterm2
        } = action.payload as TOrderItemSO

        state.soItem = {
          plant,
          plant_desc,
          price: price ?? 0,
          quantity,
          sloc,
          sloc_description,
          notes: notes ?? '',
          product_spec,
          shipping_tolerance: +shipping_tolerance,
          route,
          route_desc,
          incoterm1: incoterm1?.code,
          incoterm2,
        }
      }
    },
    setOrderItems: (state, action) => ({...state, orderItems: action.payload}),
    addOrderItem: (state, action) => {
      state.orderItems.push(action.payload)
    },
    delOrderItem: (state, action) => {
      state.orderItems = state.orderItems.filter((item) => item.id !== action.payload)
    },
    removeOrderItem: (state, action) => {
      state.orderItems = state.orderItems.splice(action.payload, 1)
    },
    putOrderItem: (state, action) => {
      const updatedItem = action.payload
      const index = state.orderItems.findIndex((item) => item.id === updatedItem.id)
      state.orderItems[index] = updatedItem
    },
    setSoItem: (state: ISoState, action: PayloadAction<ISoState['soItem']>) => {
      state.soItem = action.payload
    },
  },
})

/**
 * Action
 */
export const {
  setSoOrders,
  setFilter,
  setOutstandingFilter,
  setSort,
  setError,
  setIsLoading,
  setNumberUrls,
  resetFilter,
  setBrowseContractFilter,
  setDetailSOContract,
  setOrderItem,
  setOrderItems,
  addOrderItem,
  delOrderItem,
  putOrderItem,
  setSoItem,
  removeOrderItem,
} = soSlice.actions

/**
 * Selector
 */
export const soSelector = (state: RootState) => state.soReducer

/**
 * Reducer
 */
export default soSlice.reducer
